import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Layout, Plus, Copy, Trash2, Save, ArrowLeft, Eye, Users, Check,
  Utensils, Calendar, BedDouble, Plane, MessageCircle, MapPin, ChevronDown,
  GripVertical, X, Star, Sparkles, Search, Pencil, AlertTriangle
} from 'lucide-react';
import CelebrationEditor from './CelebrationEditor';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Design Tokens ──
const G = {
  gold:       '#D4AF37',
  goldSec:    '#C6A75A',
  goldTer:    '#B8902A',
  dark:       '#1E1A14',
  muted:      '#7A6A40',
  bg:         '#0A0A0B',
  cardGlass:  'rgba(255, 252, 240, 0.65)',
  cardBorder: 'rgba(255, 252, 240, 0.4)',
  guestGlass: 'rgba(20, 15, 8, 0.75)',
  guestBorder:'rgba(212, 168, 83, 0.6)',
  cream:      '#FFFCF0',
  cinzel:     "'Cinzel', serif",
  cormorant:  "'Cormorant Garamond', serif",
};

// ── Shared Inline Styles ──
const S = {
  card: {
    background: G.cardGlass, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    borderRadius: 16, border: `1px solid ${G.cardBorder}`, padding: 28,
    boxShadow: '0 12px 40px rgba(0,0,0,0.4)', position: 'relative', overflow: 'hidden',
  },
  label: {
    display: 'block', fontFamily: G.cinzel, fontSize: 11, color: G.dark,
    marginBottom: 6, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase',
  },
  input: {
    background: 'rgba(255,252,240,0.4)', border: '1px solid rgba(122,106,64,0.3)',
    color: G.dark, padding: '10px 14px', borderRadius: 8, fontFamily: G.cormorant,
    fontSize: 16, width: '100%', outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  btnPrimary: {
    background: `linear-gradient(135deg, ${G.gold} 0%, ${G.goldTer} 100%)`,
    color: G.dark, border: 'none', borderRadius: 8, padding: '10px 20px',
    fontFamily: G.cinzel, fontWeight: 700, fontSize: 12, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 6,
    boxShadow: '0 4px 12px rgba(212,175,55,0.3)', letterSpacing: '0.5px',
  },
  btnSecondary: {
    background: 'transparent', color: G.gold, border: `1px solid ${G.gold}`,
    borderRadius: 8, padding: '10px 20px', fontFamily: G.cinzel, fontWeight: 700,
    fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
  },
  btnGhost: {
    background: 'transparent', color: G.muted, border: 'none', borderRadius: 6,
    padding: '6px 10px', fontFamily: G.cormorant, fontSize: 14, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 4,
  },
  badge: {
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px',
    borderRadius: 20, fontSize: 10, fontWeight: 700, fontFamily: G.cinzel,
    letterSpacing: '0.8px', textTransform: 'uppercase',
  },
  guestCard: {
    background: G.guestGlass, backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
    borderRadius: 14, border: `1px solid ${G.guestBorder}`, padding: 20,
    color: G.cream, boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  },
};

// ── Action Button Styles ──
const ACTION = {
  save: {
    background: '#1E7344', color: '#fff', border: 'none', borderRadius: 8,
    padding: '9px 18px', fontFamily: G.cinzel, fontWeight: 700, fontSize: 11,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
    letterSpacing: '0.5px', boxShadow: '0 2px 8px rgba(30,115,68,0.3)',
    transition: 'filter 0.15s',
  },
  edit: {
    background: '#B8902A', color: '#fff', border: 'none', borderRadius: 8,
    padding: '9px 18px', fontFamily: G.cinzel, fontWeight: 700, fontSize: 11,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
    letterSpacing: '0.5px', boxShadow: '0 2px 8px rgba(184,144,42,0.3)',
    transition: 'filter 0.15s',
  },
  delete: {
    background: '#9E4444', color: '#fff', border: 'none', borderRadius: 8,
    padding: '9px 18px', fontFamily: G.cinzel, fontWeight: 700, fontSize: 11,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
    letterSpacing: '0.5px', boxShadow: '0 2px 8px rgba(158,68,68,0.3)',
    transition: 'filter 0.15s',
  },
};

const ICON_MAP = {
  utensils: Utensils, calendar: Calendar, 'bed-double': BedDouble,
  plane: Plane, 'concierge-bell': MessageCircle, 'map-pin': MapPin,
};

// Tab order locked by user (Feb 2026, Item B revised):
//   1. Menu Tiles
//   2. Event Details  (also hosts the small Calendar carousel-tile widget at
//                      the bottom — Calendar doesn't need a full peer tab.)
//   3. Hotels   ← split out from the previous combined Travel tab
//   4. Flights  ← split out from the previous combined Travel tab
//   5. NJ Guide
//   6. Concierge
// The previous "Discover Cards" tab is dissolved — its tile_title/tile_subtitle/
// enabled inputs are absorbed into each owning tile's tab. The discover_cards[]
// array remains the single source of truth in the template payload.
const EDITOR_TABS = [
  { key: 'menu',         label: 'Menu Tiles',     icon: Utensils },
  { key: 'event',        label: 'Event Details',  icon: Calendar },
  { key: 'hotels',       label: 'Hotels',         icon: BedDouble },
  { key: 'flights',      label: 'Flights',        icon: Plane },
  { key: 'things_to_do', label: 'NJ Guide',       icon: MapPin },
  { key: 'concierge',    label: 'Concierge',      icon: MessageCircle },
  { key: 'celebration',  label: 'Celebration',    icon: Sparkles },
];

// ═══════════════════════════════════════════════════════════════════════════
// TEMPLATE LIST VIEW
// ═══════════════════════════════════════════════════════════════════════════
function TemplateListView({ templates, onSelect, onCreate, onDuplicate, onDelete }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: G.cinzel, fontSize: 22, color: G.gold, margin: 0, fontWeight: 700 }}>
            Guest Experience Templates
          </h2>
          <p style={{ fontFamily: G.cormorant, fontSize: 15, color: G.goldSec, margin: '4px 0 0', opacity: 0.8 }}>
            Manage the content your guests see. Default applies to everyone.
          </p>
        </div>
        <button
          data-testid="create-template-btn"
          style={S.btnPrimary}
          onClick={onCreate}
        >
          <Plus size={14} /> New Template
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
        {templates.map(t => (
          <div
            key={t.id}
            data-testid={`template-card-${t.id}`}
            style={{
              ...S.card, cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
              borderLeft: t.is_default ? `3px solid ${G.gold}` : `3px solid transparent`,
            }}
            onClick={() => onSelect(t)}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.4)'; }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontFamily: G.cinzel, fontSize: 16, color: G.dark, margin: 0, fontWeight: 700 }}>
                  {t.name}
                </h3>
                <p style={{ fontFamily: G.cormorant, fontSize: 13, color: G.muted, margin: '4px 0 0' }}>
                  {t.menu_tiles?.length || 0} tiles · {t.discover_cards?.length || 0} cards
                </p>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {t.is_default ? (
                  <span style={{ ...S.badge, background: `linear-gradient(135deg, ${G.gold}, ${G.goldTer})`, color: G.dark }}>
                    <Star size={10} /> Default
                  </span>
                ) : (
                  <span style={{ ...S.badge, background: 'rgba(122,106,64,0.15)', color: G.muted,
                    border: t.status === 'active' ? `1px solid ${G.goldSec}` : '1px solid rgba(122,106,64,0.3)' }}>
                    {t.status === 'active' ? 'Active' : 'Draft'}
                  </span>
                )}
              </div>
            </div>

            {/* Assignment count */}
            {!t.is_default && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <Users size={12} color={G.muted} />
                <span style={{ fontFamily: G.cormorant, fontSize: 13, color: G.muted }}>
                  {t.assignment_count > 0 ? `Assigned to ${t.assignment_count} guest${t.assignment_count > 1 ? 's' : ''}/parties` : 'Unassigned'}
                </span>
              </div>
            )}

            {/* Quick preview: tile names */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(t.menu_tiles || []).slice(0, 4).map(tile => (
                <span key={tile.id} style={{
                  padding: '3px 8px', borderRadius: 6, fontSize: 10, fontFamily: G.cinzel,
                  background: 'rgba(122,106,64,0.1)', color: G.muted, fontWeight: 600,
                }}>
                  {tile.title}
                </span>
              ))}
            </div>

            {/* Card actions */}
            {!t.is_default && (
              <div style={{ display: 'flex', gap: 8, marginTop: 14, borderTop: '1px solid rgba(122,106,64,0.15)', paddingTop: 12 }}
                onClick={e => e.stopPropagation()}>
                <button data-testid={`duplicate-${t.id}`} style={S.btnGhost} onClick={() => onDuplicate(t.id)}>
                  <Copy size={12} /> Duplicate
                </button>
                <button data-testid={`delete-${t.id}`} style={{ ...S.btnGhost, color: '#9E4444' }} onClick={() => onDelete(t.id)}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// MENU TILES EDITOR
// ═══════════════════════════════════════════════════════════════════════════
function MenuTilesEditor({ tiles, onChange }) {
  const [expandedTile, setExpandedTile] = useState(null);
  const [confirmDeleteTile, setConfirmDeleteTile] = useState(null);

  const addTile = () => {
    const newTile = { id: `tile_${Date.now()}`, title: 'New Tile', icon: 'utensils', items: [] };
    onChange([...tiles, newTile]);
  };

  const updateTile = (idx, field, value) => {
    const updated = tiles.map((t, i) => i === idx ? { ...t, [field]: value } : t);
    onChange(updated);
  };

  const removeTile = (idx) => {
    onChange(tiles.filter((_, i) => i !== idx));
  };

  const addItem = (tileIdx) => {
    const updated = tiles.map((t, i) => i === tileIdx ? { ...t, items: [...t.items, ''] } : t);
    onChange(updated);
  };

  const updateItem = (tileIdx, itemIdx, value) => {
    const updated = tiles.map((t, i) => {
      if (i !== tileIdx) return t;
      const items = t.items.map((item, j) => j === itemIdx ? value : item);
      return { ...t, items };
    });
    onChange(updated);
  };

  const removeItem = (tileIdx, itemIdx) => {
    const updated = tiles.map((t, i) => {
      if (i !== tileIdx) return t;
      return { ...t, items: t.items.filter((_, j) => j !== itemIdx) };
    });
    onChange(updated);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <p style={{ fontFamily: G.cormorant, fontSize: 15, color: G.muted, margin: 0 }}>
          Edit the menu tiles guests see in the RSVP card.
        </p>
        <button data-testid="add-menu-tile" style={ACTION.edit} onClick={addTile}>
          <Plus size={12} /> Add Tile
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {tiles.map((tile, idx) => {
          const Icon = ICON_MAP[tile.icon] || Utensils;
          const isExpanded = expandedTile === idx;
          return (
            <div key={tile.id} data-testid={`menu-tile-${idx}`} style={{
              ...S.card, padding: 0, overflow: 'hidden',
              border: isExpanded ? `1px solid ${G.goldSec}` : `1px solid ${G.cardBorder}`,
            }}>
              {/* Tile header */}
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px',
                  cursor: 'pointer', background: isExpanded ? 'rgba(212,175,55,0.06)' : 'transparent',
                }}
                onClick={() => setExpandedTile(isExpanded ? null : idx)}
              >
                <GripVertical size={14} color={G.muted} style={{ opacity: 0.4 }} />
                <Icon size={16} color={G.gold} />
                <span style={{ fontFamily: G.cinzel, fontSize: 14, fontWeight: 700, color: G.dark, flex: 1 }}>
                  {tile.title}
                </span>
                <span style={{ fontFamily: G.cormorant, fontSize: 13, color: G.muted }}>
                  {tile.items.length} item{tile.items.length !== 1 ? 's' : ''}
                </span>
                <ChevronDown size={14} color={G.muted}
                  style={{ transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }} />
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <div style={{ padding: '0 20px 20px', borderTop: '1px solid rgba(122,106,64,0.12)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16, marginBottom: 16 }}>
                    <div>
                      <label style={S.label}>Tile Title</label>
                      <input
                        data-testid={`tile-title-${idx}`}
                        style={S.input}
                        value={tile.title}
                        onChange={e => updateTile(idx, 'title', e.target.value)}
                      />
                    </div>
                    <div>
                      <label style={S.label}>Icon</label>
                      <select
                        data-testid={`tile-icon-${idx}`}
                        style={{ ...S.input, cursor: 'pointer' }}
                        value={tile.icon}
                        onChange={e => updateTile(idx, 'icon', e.target.value)}
                      >
                        <option value="utensils">Utensils</option>
                        <option value="calendar">Calendar</option>
                        <option value="map-pin">Map Pin</option>
                        <option value="concierge-bell">Concierge</option>
                      </select>
                    </div>
                  </div>

                  <label style={S.label}>Menu Items</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {tile.items.map((item, itemIdx) => (
                      <div key={itemIdx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          data-testid={`tile-item-${idx}-${itemIdx}`}
                          style={{ ...S.input, flex: 1 }}
                          value={item}
                          onChange={e => updateItem(idx, itemIdx, e.target.value)}
                          placeholder="Item name"
                        />
                        <button style={{ ...S.btnGhost, color: '#9E4444', padding: 6 }}
                          onClick={() => removeItem(idx, itemIdx)}>
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    <button data-testid={`add-item-${idx}`} style={{ ...S.btnGhost, color: G.gold }}
                      onClick={() => addItem(idx)}>
                      <Plus size={12} /> Add Item
                    </button>
                  </div>

                  <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button data-testid={`delete-tile-${idx}`} style={ACTION.delete} onClick={() => setConfirmDeleteTile(idx)}>
                      <Trash2 size={11} /> Delete Tile
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Delete confirmation modal */}
      {confirmDeleteTile !== null && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setConfirmDeleteTile(null)}>
          <div style={{ ...S.card, maxWidth: 340, padding: 28, textAlign: 'center' }}
            onClick={e => e.stopPropagation()}>
            <Trash2 size={28} color="#9E4444" style={{ marginBottom: 12 }} />
            <h3 style={{ fontFamily: G.cinzel, fontSize: 16, color: G.dark, margin: '0 0 16px', fontWeight: 700 }}>
              Delete Tile?
            </h3>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button data-testid="confirm-delete-yes" style={ACTION.delete}
                onClick={() => { removeTile(confirmDeleteTile); setConfirmDeleteTile(null); }}>
                Yes
              </button>
              <button data-testid="confirm-delete-no" style={{ ...ACTION.save }}
                onClick={() => setConfirmDeleteTile(null)}>
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// DISCOVER CARDS EDITOR
// ═══════════════════════════════════════════════════════════════════════════
function DiscoverCardsEditor({ cards, onChange }) {
  const [expandedCard, setExpandedCard] = useState(null);
  const [confirmDeleteCard, setConfirmDeleteCard] = useState(null);

  const addCard = () => {
    const newCard = {
      id: `card_${Date.now()}`, type: 'info', title: 'New Section', subtitle: '',
      icon: 'map-pin', enabled: true, info_content: { heading: '', body_text: '', links: [] },
    };
    onChange([...cards, newCard]);
  };

  const updateCard = (idx, field, value) => {
    const updated = cards.map((c, i) => i === idx ? { ...c, [field]: value } : c);
    onChange(updated);
  };

  const removeCard = (idx) => onChange(cards.filter((_, i) => i !== idx));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <p style={{ fontFamily: G.cormorant, fontSize: 15, color: G.muted, margin: 0 }}>
          Manage the explore/discover carousel cards.
        </p>
        <button data-testid="add-discover-card" style={ACTION.edit} onClick={addCard}>
          <Plus size={12} /> Add Card
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {cards.map((card, idx) => {
          const Icon = ICON_MAP[card.icon] || MapPin;
          const isExpanded = expandedCard === idx;
          return (
            <div key={card.id} data-testid={`discover-card-${idx}`} style={{
              ...S.card, padding: 0, overflow: 'hidden',
              border: isExpanded ? `1px solid ${G.goldSec}` : `1px solid ${G.cardBorder}`,
              opacity: card.enabled ? 1 : 0.5,
            }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', cursor: 'pointer' }}
                onClick={() => setExpandedCard(isExpanded ? null : idx)}
              >
                <Icon size={16} color={G.gold} />
                <span style={{ fontFamily: G.cinzel, fontSize: 14, fontWeight: 700, color: G.dark, flex: 1 }}>
                  {card.title}
                </span>
                <span style={{ ...S.badge, background: card.enabled ? 'rgba(30,115,68,0.12)' : 'rgba(158,68,68,0.12)',
                  color: card.enabled ? '#1E7344' : '#9E4444', fontSize: 9 }}>
                  {card.enabled ? 'Visible' : 'Hidden'}
                </span>
                <ChevronDown size={14} color={G.muted}
                  style={{ transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }} />
              </div>

              {isExpanded && (
                <div style={{ padding: '0 20px 20px', borderTop: '1px solid rgba(122,106,64,0.12)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
                    <div>
                      <label style={S.label}>Title</label>
                      <input data-testid={`card-title-${idx}`} style={S.input} value={card.title}
                        onChange={e => updateCard(idx, 'title', e.target.value)} />
                    </div>
                    <div>
                      <label style={S.label}>Subtitle</label>
                      <input data-testid={`card-subtitle-${idx}`} style={S.input} value={card.subtitle}
                        onChange={e => updateCard(idx, 'subtitle', e.target.value)} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 12, alignItems: 'center' }}>
                    <label style={{ ...S.label, margin: 0, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={card.enabled}
                        onChange={e => updateCard(idx, 'enabled', e.target.checked)}
                        style={{ accentColor: G.gold }} />
                      Visible to Guests
                    </label>
                    {card.type === 'info' && !['calendar', 'hotels', 'flights', 'concierge'].includes(card.id) && (
                      <button data-testid={`delete-card-${idx}`} style={{ ...ACTION.delete, padding: '6px 12px', fontSize: 10, marginLeft: 'auto' }}
                        onClick={() => setConfirmDeleteCard(idx)}>
                        <Trash2 size={11} /> Delete Card
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Delete confirmation modal */}
      {confirmDeleteCard !== null && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setConfirmDeleteCard(null)}>
          <div style={{ ...S.card, maxWidth: 340, padding: 28, textAlign: 'center' }}
            onClick={e => e.stopPropagation()}>
            <Trash2 size={28} color="#9E4444" style={{ marginBottom: 12 }} />
            <h3 style={{ fontFamily: G.cinzel, fontSize: 16, color: G.dark, margin: '0 0 16px', fontWeight: 700 }}>
              Delete Card?
            </h3>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button data-testid="confirm-delete-card-yes" style={ACTION.delete}
                onClick={() => { removeCard(confirmDeleteCard); setConfirmDeleteCard(null); }}>
                Yes
              </button>
              <button data-testid="confirm-delete-card-no" style={{ ...ACTION.save }}
                onClick={() => setConfirmDeleteCard(null)}>
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// THINGS TO DO / NJ GUIDE EDITOR
// ═══════════════════════════════════════════════════════════════════════════
// Controls every visible string + the map iframe + the categories list +
// the places list for the NJ Guide / Things to Do panel.
// All defaults equal current literal hardcoded strings in
// ExploreEntrySection.NJGuidePanel — when admin has not edited a field, the
// frontend reads `config?.things_to_do?.<key> || '<exact current literal>'`
// so live behavior is byte-identical until admin opts to change something.
// ═══════════════════════════════════════════════════════════════════════════
function ThingsToDoEditor({ data, onChange }) {
  const update = (path, value) => {
    const next = JSON.parse(JSON.stringify(data || {}));
    const keys = path.split('.');
    let obj = next;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!obj[keys[i]]) obj[keys[i]] = {};
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    onChange(next);
  };

  // Categories list helpers
  const categories = data?.categories || [];
  const updateCategory = (idx, field, value) => {
    const list = JSON.parse(JSON.stringify(categories));
    list[idx] = { ...list[idx], [field]: value };
    update('categories', list);
  };
  const addCategory = () => update('categories', [...categories, { key: `cat_${Date.now()}`, label: 'New Category' }]);
  const removeCategory = (idx) => update('categories', categories.filter((_, i) => i !== idx));
  const moveCategory = (idx, dir) => {
    const list = [...categories];
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    update('categories', list);
  };

  // Places list helpers
  const places = data?.places || [];
  const updatePlace = (idx, field, value) => {
    const list = JSON.parse(JSON.stringify(places));
    list[idx] = { ...list[idx], [field]: value };
    update('places', list);
  };
  const addPlace = () => update('places', [...places, { name: '', address: '', category: categories[0]?.key || '', is_host_favorite: false, note: '' }]);
  const removePlace = (idx) => update('places', places.filter((_, i) => i !== idx));

  return (
    <div>
      <p style={{ fontFamily: G.cormorant, fontSize: 15, color: G.muted, margin: '0 0 20px' }}>
        Configure the NJ Guide / Things to Do panel that guests see. Fields left blank fall back to the current default text — your tile keeps working with no changes if you don't edit anything.
      </p>

      {/* ── PANEL HEADER ── */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ ...S.label, marginBottom: 12, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: G.gold }}>
          Panel Header
        </p>
        <label style={S.label}>Panel header title</label>
        <input
          data-testid="ttd-tile-title"
          style={S.input}
          value={data?.tile_title ?? ''}
          onChange={e => update('tile_title', e.target.value)}
          placeholder="NJ Guide"
        />
      </div>

      {/* ── EMBEDDED MAP ── */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ ...S.label, marginBottom: 12, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: G.gold }}>
          Embedded Map
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer', ...S.label }}>
          <input
            type="checkbox"
            data-testid="ttd-map-visible"
            checked={data?.map_visible !== false}
            onChange={e => update('map_visible', e.target.checked)}
            style={{ accentColor: G.gold }}
          />
          Show embedded map
        </label>
        <label style={S.label}>Map iframe URL</label>
        <input
          data-testid="ttd-map-url"
          style={S.input}
          value={data?.map_url ?? ''}
          onChange={e => update('map_url', e.target.value)}
          placeholder="https://maps.google.com/maps?q=...&output=embed"
        />
        <p style={{ fontFamily: G.cormorant, fontSize: 13, color: G.muted, margin: '6px 0 0', fontStyle: 'italic' }}>
          Use the embed URL from Google Maps (Share → Embed a map → Copy HTML → take the src URL).
        </p>
      </div>

      {/* ── CATEGORIES ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <p style={{ ...S.label, margin: 0, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: G.gold }}>
            Categories
          </p>
          <button data-testid="ttd-add-category" style={{ ...S.btnGhost, color: G.gold }} onClick={addCategory}>
            <Plus size={12} /> Add Category
          </button>
        </div>
        <p style={{ fontFamily: G.cormorant, fontSize: 13, color: G.muted, margin: '0 0 12px', fontStyle: 'italic' }}>
          Each category needs a stable key (used to group places) and a guest-facing label (what shows on the filter chip).
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {categories.map((cat, idx) => (
            <div key={idx} data-testid={`ttd-category-${idx}`} style={{
              display: 'grid', gridTemplateColumns: '1fr 1.5fr auto auto auto', gap: 8, alignItems: 'center',
              padding: 10, background: 'rgba(122,106,64,0.05)', borderRadius: 8,
              border: '1px solid rgba(122,106,64,0.15)',
            }}>
              <input
                data-testid={`ttd-category-key-${idx}`}
                style={{ ...S.input, fontSize: 12 }}
                value={cat.key || ''}
                onChange={e => updateCategory(idx, 'key', e.target.value)}
                placeholder="key (e.g. food_dining)"
              />
              <input
                data-testid={`ttd-category-label-${idx}`}
                style={{ ...S.input, fontSize: 12 }}
                value={cat.label || ''}
                onChange={e => updateCategory(idx, 'label', e.target.value)}
                placeholder="Label (e.g. Food & Dining)"
              />
              <button
                style={{ ...S.btnGhost, padding: 6, opacity: idx === 0 ? 0.3 : 1 }}
                disabled={idx === 0}
                onClick={() => moveCategory(idx, -1)}
                title="Move up"
              >▲</button>
              <button
                style={{ ...S.btnGhost, padding: 6, opacity: idx === categories.length - 1 ? 0.3 : 1 }}
                disabled={idx === categories.length - 1}
                onClick={() => moveCategory(idx, 1)}
                title="Move down"
              >▼</button>
              <button
                data-testid={`ttd-category-remove-${idx}`}
                style={{ ...S.btnGhost, color: '#9E4444', padding: 6 }}
                onClick={() => removeCategory(idx)}
                title="Remove"
              >
                <X size={14} />
              </button>
            </div>
          ))}
          {categories.length === 0 && (
            <p style={{ fontSize: 13, color: G.muted, fontStyle: 'italic', margin: 0, padding: '12px 0' }}>
              No categories defined — using built-in defaults: All / Venue & Hotels / Mom's Favorites / Food & Dining / Activities / Must-Visits.
            </p>
          )}
        </div>
      </div>

      {/* ── EMPTY STATES ── */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ ...S.label, marginBottom: 12, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: G.gold }}>
          Empty State Messages
        </p>
        <label style={S.label}>Empty-state title (when no places exist)</label>
        <input
          data-testid="ttd-empty-title"
          style={S.input}
          value={data?.empty_state_title ?? ''}
          onChange={e => update('empty_state_title', e.target.value)}
          placeholder="Curated places coming soon"
        />
        <label style={{ ...S.label, marginTop: 12 }}>Empty-state hint (small line beneath)</label>
        <input
          data-testid="ttd-empty-hint"
          style={S.input}
          value={data?.empty_state_hint ?? ''}
          onChange={e => update('empty_state_hint', e.target.value)}
          placeholder="Add places in Settings → Guest-Facing Content"
        />
        <label style={{ ...S.label, marginTop: 12 }}>Per-category empty text</label>
        <input
          data-testid="ttd-category-empty"
          style={S.input}
          value={data?.category_empty_text ?? ''}
          onChange={e => update('category_empty_text', e.target.value)}
          placeholder="No places in this category yet."
        />
      </div>

      {/* ── PLACES LIST ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <p style={{ ...S.label, margin: 0, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: G.gold }}>
            Places
          </p>
          <button data-testid="ttd-add-place" style={{ ...S.btnGhost, color: G.gold }} onClick={addPlace}>
            <Plus size={12} /> Add Place
          </button>
        </div>
        <p style={{ fontFamily: G.cormorant, fontSize: 13, color: G.muted, margin: '0 0 12px', fontStyle: 'italic' }}>
          Each place must reference a category key from the list above. Mark host favorites to highlight them with a lavender border.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {places.map((place, idx) => (
            <div key={idx} data-testid={`ttd-place-${idx}`} style={{
              padding: 14, background: 'rgba(122,106,64,0.05)', borderRadius: 10,
              border: '1px solid rgba(122,106,64,0.18)',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={S.label}>Place name</label>
                  <input
                    data-testid={`ttd-place-name-${idx}`}
                    style={S.input}
                    value={place.name || ''}
                    onChange={e => updatePlace(idx, 'name', e.target.value)}
                    placeholder="Riverside Bistro"
                  />
                </div>
                <div>
                  <label style={S.label}>Address</label>
                  <input
                    data-testid={`ttd-place-address-${idx}`}
                    style={S.input}
                    value={place.address || ''}
                    onChange={e => updatePlace(idx, 'address', e.target.value)}
                    placeholder="123 Main St, Township of Washington, NJ"
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={S.label}>Category</label>
                  <select
                    data-testid={`ttd-place-category-${idx}`}
                    style={S.input}
                    value={place.category || ''}
                    onChange={e => updatePlace(idx, 'category', e.target.value)}
                  >
                    <option value="">— Select category —</option>
                    {categories.map(c => (
                      <option key={c.key} value={c.key}>{c.label || c.key}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', ...S.label, margin: 0 }}>
                    <input
                      type="checkbox"
                      data-testid={`ttd-place-favorite-${idx}`}
                      checked={!!place.is_host_favorite}
                      onChange={e => updatePlace(idx, 'is_host_favorite', e.target.checked)}
                      style={{ accentColor: G.gold }}
                    />
                    Host favorite
                  </label>
                </div>
              </div>
              <div>
                <label style={S.label}>Short note (optional)</label>
                <input
                  data-testid={`ttd-place-note-${idx}`}
                  style={S.input}
                  value={place.note || ''}
                  onChange={e => updatePlace(idx, 'note', e.target.value)}
                  placeholder="Mom's favorite breakfast spot"
                />
              </div>
              <div style={{ marginTop: 10, textAlign: 'right' }}>
                <button
                  data-testid={`ttd-place-remove-${idx}`}
                  style={{ ...ACTION.delete, padding: '6px 12px', fontSize: 11 }}
                  onClick={() => removePlace(idx)}
                >
                  <Trash2 size={11} /> Remove Place
                </button>
              </div>
            </div>
          ))}
          {places.length === 0 && (
            <p style={{ fontSize: 13, color: G.muted, fontStyle: 'italic', margin: 0, padding: '12px 0' }}>
              No places yet. Click "Add Place" to add the first one.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// CONCIERGE EDITOR
// ═══════════════════════════════════════════════════════════════════════════
function ConciergeEditor({ concierge, onChange }) {
  const update = (field, value) => onChange({ ...concierge, [field]: value });

  const addQuestion = () => update('preset_questions', [...(concierge.preset_questions || []), '']);
  const updateQuestion = (idx, value) => {
    const qs = [...(concierge.preset_questions || [])];
    qs[idx] = value;
    update('preset_questions', qs);
  };
  const removeQuestion = (idx) => update('preset_questions', concierge.preset_questions.filter((_, i) => i !== idx));

  return (
    <div>
      <p style={{ fontFamily: G.cormorant, fontSize: 15, color: G.muted, margin: '0 0 20px' }}>
        Configure Oscar's greeting and preset questions. Use <code style={{ background: 'rgba(122,106,64,0.1)', padding: '2px 6px', borderRadius: 4, fontSize: 13 }}>{'{{first_name}}'}</code> for personalization.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={S.label}>Greeting Template</label>
          <input data-testid="concierge-greeting" style={S.input} value={concierge.greeting_template || ''}
            onChange={e => update('greeting_template', e.target.value)} placeholder="Hey {{first_name}}" />
        </div>
        <div>
          <label style={S.label}>Title Template</label>
          <input data-testid="concierge-title" style={S.input} value={concierge.title_template || ''}
            onChange={e => update('title_template', e.target.value)} placeholder="{{first_name}}'s Private Concierge" />
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={S.label}>Intro Text (shared between Concierge card and Oscar chat)</label>
        <textarea data-testid="concierge-intro" style={{ ...S.input, minHeight: 80, resize: 'vertical' }}
          value={concierge.intro_text || ''}
          onChange={e => update('intro_text', e.target.value)} />
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <label style={{ ...S.label, margin: 0 }}>Preset Questions</label>
          <button data-testid="add-preset-question" style={{ ...S.btnGhost, color: G.gold }} onClick={addQuestion}>
            <Plus size={12} /> Add Question
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(concierge.preset_questions || []).map((q, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input data-testid={`preset-q-${idx}`} style={{ ...S.input, flex: 1 }} value={q}
                onChange={e => updateQuestion(idx, e.target.value)} placeholder="e.g. What should I wear?" />
              <button style={{ ...S.btnGhost, color: '#9E4444', padding: 6 }} onClick={() => removeQuestion(idx)}>
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// CAROUSEL TILE SECTION (Item B revised, Feb 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Reusable per-tile widget surfacing the three carousel-row controls:
//   • tile title (admin-editable label shown on the row)
//   • tile subtitle (admin-editable description shown under the title)
//   • enabled / visibility toggle (controls whether the tile renders at all)
//
// All three values live inside the template's `discover_cards[]` array, keyed
// by `cardId`. This component is read/write-by-id so each owning tile editor
// (Hotels, Flights, NJ Guide, Concierge, Calendar widget) can compose it
// alongside its own data.
// ═══════════════════════════════════════════════════════════════════════════
function CarouselTileSection({
  cardId,
  cards,
  onCardsChange,
  defaultTitle,
  defaultSubtitle,
  enabledLabel = 'Show this tile to guests',
}) {
  const card = (Array.isArray(cards) ? cards : []).find(c => c?.id === cardId) || {};
  const writeCard = (patch) => {
    const list = Array.isArray(cards) ? [...cards] : [];
    const idx  = list.findIndex(c => c?.id === cardId);
    if (idx === -1) {
      list.push({ id: cardId, type: cardId, enabled: true, ...patch });
    } else {
      list[idx] = { ...list[idx], ...patch };
    }
    onCardsChange(list);
  };
  const isEnabled = card.enabled !== false; // default-true so existing templates don't disappear

  return (
    <div data-testid={`carousel-tile-section-${cardId}`} style={{
      marginBottom: 28,
      padding: '14px 16px',
      borderRadius: 10,
      background: 'rgba(122,106,64,0.05)',
      border: '1px solid rgba(122,106,64,0.18)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 10,
      }}>
        <p style={{ fontFamily: G.cinzel, fontSize: 11, fontWeight: 700, letterSpacing: '1.5px',
          textTransform: 'uppercase', color: G.gold, margin: 0 }}>
          Carousel Tile
        </p>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontFamily: G.cormorant, fontSize: 13, color: G.muted, cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            data-testid={`tile-enabled-${cardId}`}
            checked={isEnabled}
            onChange={e => writeCard({ enabled: e.target.checked })}
          />
          {enabledLabel}
        </label>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
        <div>
          <label style={S.label}>Tile title</label>
          <input
            data-testid={`tile-title-${cardId}`}
            style={S.input}
            value={card.title || ''}
            placeholder={defaultTitle}
            onChange={e => writeCard({ title: e.target.value })}
          />
        </div>
        <div>
          <label style={S.label}>Tile subtitle</label>
          <input
            data-testid={`tile-subtitle-${cardId}`}
            style={S.input}
            value={card.subtitle || ''}
            placeholder={defaultSubtitle}
            onChange={e => writeCard({ subtitle: e.target.value })}
          />
        </div>
      </div>
      <p style={{ fontFamily: G.cormorant, fontSize: 11, fontStyle: 'italic',
        color: G.muted, margin: '8px 0 0' }}>
        Leave blank to use the default ({defaultTitle} · {defaultSubtitle}).
      </p>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// HOTELS EDITOR (Item B revised — split out from the legacy combined Travel
// tab. The carousel-tile controls sit OUTSIDE this component so the parent
// composes them adjacently.)
// ═══════════════════════════════════════════════════════════════════════════
function HotelsEditor({ travel, onTravelChange }) {
  const update = (path, value) => {
    const next = JSON.parse(JSON.stringify(travel || {}));
    const keys = path.split('.');
    let obj = next;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!obj[keys[i]]) obj[keys[i]] = {};
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    onTravelChange(next);
  };

  const h = travel?.hotels || {};

  return (
    <div>
      <h3 style={{ fontFamily: G.cinzel, fontSize: 15, color: G.dark, margin: '0 0 16px', fontWeight: 700,
        display: 'flex', alignItems: 'center', gap: 8 }}>
        <BedDouble size={16} color={G.gold} /> Hotel Details
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={S.label}>Primary Hotel Name</label>
          <input data-testid="hotel-name" style={S.input} value={h.primary_hotel?.name || ''}
            onChange={e => update('hotels.primary_hotel.name', e.target.value)} />
        </div>
        <div>
          <label style={S.label}>Booking URL</label>
          <input data-testid="hotel-url" style={S.input} value={h.primary_hotel?.booking_url || ''}
            onChange={e => update('hotels.primary_hotel.booking_url', e.target.value)} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={S.label}>Address</label>
          <input data-testid="hotel-address" style={S.input} value={h.primary_hotel?.address || ''}
            onChange={e => update('hotels.primary_hotel.address', e.target.value)} />
        </div>
        <div>
          <label style={S.label}>Discount Code</label>
          <input data-testid="hotel-discount" style={S.input} value={h.primary_hotel?.discount_code || ''}
            onChange={e => update('hotels.primary_hotel.discount_code', e.target.value)} />
        </div>
        <div>
          <label style={{ ...S.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" data-testid="hotel-room-block-enabled"
              checked={h.room_block_enabled !== false}
              onChange={e => update('hotels.room_block_enabled', e.target.checked)} />
            Room block enabled
          </label>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// FLIGHTS EDITOR (Item B revised — split out from the legacy combined Travel
// tab. The carousel-tile controls sit OUTSIDE this component so the parent
// composes them adjacently.)
// ═══════════════════════════════════════════════════════════════════════════
function FlightsEditor({ travel, onTravelChange }) {
  const update = (path, value) => {
    const next = JSON.parse(JSON.stringify(travel || {}));
    const keys = path.split('.');
    let obj = next;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!obj[keys[i]]) obj[keys[i]] = {};
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    onTravelChange(next);
  };

  const f = travel?.flights || {};

  return (
    <div>
      <h3 style={{ fontFamily: G.cinzel, fontSize: 15, color: G.dark, margin: '0 0 16px', fontWeight: 700,
        display: 'flex', alignItems: 'center', gap: 8 }}>
        <Plane size={16} color={G.gold} /> Flight Details
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={S.label}>Preferred Airport</label>
          <input data-testid="flight-airport" style={S.input} value={f.preferred_airport || ''}
            onChange={e => update('flights.preferred_airport', e.target.value)} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={S.label}>Travel Tips</label>
          <textarea data-testid="flight-tips" style={{ ...S.input, minHeight: 60, resize: 'vertical' }}
            value={f.tips || ''} onChange={e => update('flights.tips', e.target.value)} />
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// TRAVEL EDITOR  ── DEPRECATED (Feb 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Replaced by HotelsEditor + FlightsEditor (Item B revised). Keeping the
// function definition below removed.
function TravelEditor({ travel, onChange }) {
  const update = (path, value) => {
    const next = JSON.parse(JSON.stringify(travel || {}));
    const keys = path.split('.');
    let obj = next;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!obj[keys[i]]) obj[keys[i]] = {};
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    onChange(next);
  };

  const h = travel?.hotels || {};
  const f = travel?.flights || {};

  return (
    <div>
      {/* Hotels */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={{ fontFamily: G.cinzel, fontSize: 15, color: G.dark, margin: '0 0 16px', fontWeight: 700,
          display: 'flex', alignItems: 'center', gap: 8 }}>
          <BedDouble size={16} color={G.gold} /> Hotels
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={S.label}>Primary Hotel Name</label>
            <input data-testid="hotel-name" style={S.input} value={h.primary_hotel?.name || ''}
              onChange={e => update('hotels.primary_hotel.name', e.target.value)} />
          </div>
          <div>
            <label style={S.label}>Booking URL</label>
            <input data-testid="hotel-url" style={S.input} value={h.primary_hotel?.booking_url || ''}
              onChange={e => update('hotels.primary_hotel.booking_url', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={S.label}>Address</label>
            <input data-testid="hotel-address" style={S.input} value={h.primary_hotel?.address || ''}
              onChange={e => update('hotels.primary_hotel.address', e.target.value)} />
          </div>
          <div>
            <label style={S.label}>Discount Code</label>
            <input data-testid="hotel-discount" style={S.input} value={h.primary_hotel?.discount_code || ''}
              onChange={e => update('hotels.primary_hotel.discount_code', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Flights */}
      <div>
        <h3 style={{ fontFamily: G.cinzel, fontSize: 15, color: G.dark, margin: '0 0 16px', fontWeight: 700,
          display: 'flex', alignItems: 'center', gap: 8 }}>
          <Plane size={16} color={G.gold} /> Flights
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={S.label}>Preferred Airport</label>
            <input data-testid="flight-airport" style={S.input} value={f.preferred_airport || ''}
              onChange={e => update('flights.preferred_airport', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={S.label}>Travel Tips</label>
            <textarea data-testid="flight-tips" style={{ ...S.input, minHeight: 60, resize: 'vertical' }}
              value={f.tips || ''} onChange={e => update('flights.tips', e.target.value)} />
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// EVENT DETAILS EDITOR
// ═══════════════════════════════════════════════════════════════════════════
function EventDetailsEditor({ details, onChange }) {
  const update = (field, value) => onChange({ ...details, [field]: value });

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={S.label}>Day</label>
          <input data-testid="event-day" style={S.input} value={details?.day || ''}
            onChange={e => update('day', e.target.value)} />
        </div>
        <div>
          <label style={S.label}>Date</label>
          <input data-testid="event-date" style={S.input} value={details?.date || ''}
            onChange={e => update('date', e.target.value)} />
        </div>
        <div>
          <label style={S.label}>Time</label>
          <input data-testid="event-time" style={S.input} value={details?.time || ''}
            onChange={e => update('time', e.target.value)} />
        </div>
        <div>
          <label style={S.label}>Attire</label>
          <input data-testid="event-attire" style={S.input} value={details?.attire || ''}
            onChange={e => update('attire', e.target.value)} />
        </div>
        <div>
          <label style={S.label}>Venue Name</label>
          <input data-testid="event-venue" style={S.input} value={details?.venue_name || ''}
            onChange={e => update('venue_name', e.target.value)} />
        </div>
        <div>
          <label style={S.label}>Venue Address</label>
          <input data-testid="event-venue-addr" style={S.input} value={details?.venue_address || ''}
            onChange={e => update('venue_address', e.target.value)} />
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// LIVE PREVIEW PANE
// ═══════════════════════════════════════════════════════════════════════════
function LivePreview({ template, previewGuest }) {
  const firstName = previewGuest?.first_name || 'Guest';
  const personalize = (s) => (s || '').replace(/\{\{first_name\}\}/g, firstName);

  return (
    <div style={{
      backgroundImage: "url('https://customer-assets.emergentagent.com/job_49133e57-f6cd-4b7b-9084-054c1edd8062/artifacts/hhyiowmm_Image.png')",
      backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: 20, padding: 24,
      border: `2px solid ${G.gold}`, minHeight: 500, display: 'flex', flexDirection: 'column', gap: 16,
      boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
    }}>
      {/* Preview header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: G.cinzel, fontSize: 10, color: '#FFFFFF', letterSpacing: '1.5px',
          textTransform: 'uppercase', fontWeight: 700,
          textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>
          <Eye size={10} style={{ marginRight: 4, verticalAlign: 'middle' }} />
          Live Preview {previewGuest ? `· ${firstName}` : '· Default'}
        </span>
        <span style={{ ...S.badge, background: 'rgba(212,175,55,0.35)', color: '#FFFFFF', fontSize: 8,
          border: `1px solid ${G.gold}`, textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
          {template?.is_default ? 'Default Template' : 'Custom Template'}
        </span>
      </div>

      {/* Menu tiles preview */}
      <div style={S.guestCard}>
        <p style={{ fontFamily: G.cinzel, fontSize: 11, color: G.goldSec, letterSpacing: '1px',
          textTransform: 'uppercase', margin: '0 0 12px', fontWeight: 700 }}>Menu</p>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
          {(template?.menu_tiles || []).map(tile => (
            <div key={tile.id} style={{
              minWidth: 80, padding: '10px 12px', borderRadius: 10,
              background: 'rgba(255,252,240,0.08)', border: '1px solid rgba(212,168,83,0.3)',
              textAlign: 'center', flexShrink: 0,
            }}>
              <Utensils size={14} color={G.goldSec} style={{ margin: '0 auto 6px', display: 'block' }} />
              <span style={{ fontFamily: G.cinzel, fontSize: 9, color: G.cream, fontWeight: 600 }}>
                {tile.title}
              </span>
              <p style={{ fontFamily: G.cormorant, fontSize: 10, color: 'rgba(255,252,240,0.5)',
                margin: '4px 0 0', lineHeight: 1.3 }}>
                {tile.items.slice(0, 2).join(', ')}{tile.items.length > 2 ? '...' : ''}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Discover cards preview */}
      <div style={S.guestCard}>
        <p style={{ fontFamily: G.cinzel, fontSize: 11, color: G.goldSec, letterSpacing: '1px',
          textTransform: 'uppercase', margin: '0 0 12px', fontWeight: 700 }}>Discover</p>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
          {(template?.discover_cards || []).filter(c => c.enabled).map(card => {
            const Icon = ICON_MAP[card.icon] || MapPin;
            return (
              <div key={card.id} style={{
                minWidth: 72, padding: '10px 10px', borderRadius: 10,
                background: 'rgba(255,252,240,0.06)', border: '1px solid rgba(212,168,83,0.2)',
                textAlign: 'center', flexShrink: 0,
              }}>
                <Icon size={14} color={G.goldSec} style={{ margin: '0 auto 4px', display: 'block' }} />
                <span style={{ fontFamily: G.cinzel, fontSize: 8, color: G.cream, fontWeight: 600 }}>
                  {card.title}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Concierge preview */}
      <div style={{ ...S.guestCard, background: 'rgba(20,15,8,0.85)' }}>
        <p style={{ fontFamily: G.cinzel, fontSize: 11, color: G.goldSec, letterSpacing: '1px',
          textTransform: 'uppercase', margin: '0 0 8px', fontWeight: 700 }}>
          {personalize(template?.concierge?.title_template || 'Private Concierge')}
        </p>
        <p style={{ fontFamily: G.cormorant, fontSize: 14, color: 'rgba(255,252,240,0.7)',
          margin: '0 0 12px', fontStyle: 'italic' }}>
          {personalize(template?.concierge?.greeting_template || '')}
        </p>
        <p style={{ fontFamily: G.cormorant, fontSize: 12, color: 'rgba(255,252,240,0.55)', margin: 0 }}>
          {personalize(template?.concierge?.intro_text || '')}
        </p>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// ASSIGNMENT UI
// ═══════════════════════════════════════════════════════════════════════════
function AssignmentPanel({ templateId, assignments, onAssign, onRemove }) {
  const [guests, setGuests] = useState([]);
  const [parties, setParties] = useState([]);
  const [search, setSearch] = useState('');
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/guests`).then(r => r.json()).then(setGuests).catch(() => {});
    fetch(`${API}/api/parties`).then(r => r.json()).then(setParties).catch(() => {});
  }, []);

  const assignedIds = new Set((assignments || []).map(a => a.target_id));

  const filteredGuests = guests.filter(g =>
    !assignedIds.has(g.id) &&
    `${g.first_name} ${g.last_name}`.toLowerCase().includes(search.toLowerCase())
  ).slice(0, 10);

  const filteredParties = parties.filter(p =>
    !assignedIds.has(p.id) &&
    (p.party_name || '').toLowerCase().includes(search.toLowerCase())
  ).slice(0, 10);

  const handleAssign = (type, id, name) => {
    onAssign([{ type, target_id: id, target_name: name }]);
    setSearch('');
  };

  return (
    <div style={{ marginTop: 20, borderTop: '1px solid rgba(122,106,64,0.15)', paddingTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <label style={{ ...S.label, margin: 0, fontSize: 13 }}>
          <Users size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          Assigned To
        </label>
        <button data-testid="show-assignment-picker" style={{ ...S.btnGhost, color: G.gold }}
          onClick={() => setShowPicker(!showPicker)}>
          <Plus size={12} /> Assign
        </button>
      </div>

      {/* Current assignments */}
      {(assignments || []).length === 0 ? (
        <p style={{ fontFamily: G.cormorant, fontSize: 13, color: G.muted, fontStyle: 'italic' }}>
          No assignments — this template is not active for any guests.
        </p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {assignments.map(a => (
            <span key={a.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
              borderRadius: 20, background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.3)',
              fontFamily: G.cormorant, fontSize: 13, color: G.dark,
            }}>
              {a.assignment_type === 'party' ? <Users size={11} /> : <span style={{ fontSize: 11 }}>P</span>}
              {a.target_name || a.target_id}
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}
                onClick={() => onRemove(a.id)}>
                <X size={12} color="#9E4444" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Picker */}
      {showPicker && (
        <div style={{ ...S.card, padding: 16, marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Search size={14} color={G.muted} />
            <input data-testid="assignment-search" style={{ ...S.input, padding: '8px 12px', fontSize: 14 }}
              placeholder="Search guests or parties..."
              value={search} onChange={e => setSearch(e.target.value)} autoFocus />
          </div>

          {filteredParties.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <span style={{ ...S.label, fontSize: 9, marginBottom: 4 }}>Parties</span>
              {filteredParties.map(p => (
                <div key={p.id} data-testid={`assign-party-${p.id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer',
                    borderRadius: 6, transition: 'background 0.15s' }}
                  onClick={() => handleAssign('party', p.id, p.party_name)}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,175,55,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <Users size={12} color={G.gold} />
                  <span style={{ fontFamily: G.cormorant, fontSize: 14, color: G.dark }}>{p.party_name}</span>
                </div>
              ))}
            </div>
          )}

          {filteredGuests.length > 0 && (
            <div>
              <span style={{ ...S.label, fontSize: 9, marginBottom: 4 }}>Guests</span>
              {filteredGuests.map(g => (
                <div key={g.id} data-testid={`assign-guest-${g.id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer',
                    borderRadius: 6, transition: 'background 0.15s' }}
                  onClick={() => handleAssign('guest', g.id, `${g.first_name} ${g.last_name}`)}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,175,55,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontFamily: G.cormorant, fontSize: 14, color: G.dark }}>
                    {g.first_name} {g.last_name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// UNSAVED CHANGES MODAL
// ═══════════════════════════════════════════════════════════════════════════
function UnsavedChangesModal({ tabLabel, onDiscard, onStay }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onStay}>
      <div style={{
        ...S.card, maxWidth: 420, padding: 28, textAlign: 'center',
      }} onClick={e => e.stopPropagation()}>
        <AlertTriangle size={32} color="#B8902A" style={{ marginBottom: 12 }} />
        <h3 style={{ fontFamily: G.cinzel, fontSize: 16, color: G.dark, margin: '0 0 8px', fontWeight: 700 }}>
          Unsaved Changes
        </h3>
        <p style={{ fontFamily: G.cormorant, fontSize: 15, color: G.muted, margin: '0 0 20px', lineHeight: 1.5 }}>
          You have unsaved changes in <strong>{tabLabel}</strong>. Please save before switching tabs or your changes will be lost.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button data-testid="discard-changes" style={ACTION.delete} onClick={onDiscard}>
            <Trash2 size={12} /> Discard
          </button>
          <button data-testid="stay-and-save" style={ACTION.save} onClick={onStay}>
            <ArrowLeft size={12} /> Go Back & Save
          </button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// TAB SAVE FOOTER — green Save, shown at bottom of each tab
// ═══════════════════════════════════════════════════════════════════════════
function TabSaveFooter({ dirty, saving, saved, onSave, tabLabel }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12,
      marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(122,106,64,0.15)',
    }}>
      {saved && (
        <span style={{ fontFamily: G.cormorant, fontSize: 14, color: '#1E7344', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Check size={14} /> {tabLabel} saved
        </span>
      )}
      {dirty && !saved && (
        <span style={{ fontFamily: G.cormorant, fontSize: 13, color: '#B8902A', fontStyle: 'italic' }}>
          Unsaved changes
        </span>
      )}
      <button
        data-testid={`save-tab-${tabLabel.toLowerCase().replace(/\s+/g, '-')}`}
        style={{ ...ACTION.save, opacity: dirty ? 1 : 0.5, cursor: dirty ? 'pointer' : 'default' }}
        onClick={onSave}
        disabled={!dirty || saving}
      >
        <Save size={12} /> {saving ? 'Saving...' : `Save ${tabLabel}`}
      </button>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// TEMPLATE EDITOR VIEW (Split-pane: Editor + Preview)
// ═══════════════════════════════════════════════════════════════════════════
function TemplateEditorView({ template, onBack, onSave, guests }) {
  const [form, setForm] = useState(template);
  const [activeTab, setActiveTab] = useState('menu');
  const [saving, setSaving] = useState(false);
  const [savedTab, setSavedTab] = useState(null);
  const [assignments, setAssignments] = useState(template.assignments || []);
  const [previewGuestId, setPreviewGuestId] = useState('');
  const [pendingTab, setPendingTab] = useState(null);

  // Track per-tab dirty state by comparing against last saved snapshot
  const [savedSnapshot, setSavedSnapshot] = useState(JSON.stringify(template));
  const isDirty = useCallback((tabKey) => {
    const current = JSON.stringify(form);
    if (current === savedSnapshot) return false;
    // Per-tab dirty check — each guest tile tab tracks BOTH its own legacy
    // field (e.g. travel.hotels) AND the matching discover_cards[] entry, so
    // edits to either surface mark the tab dirty.
    const saved = JSON.parse(savedSnapshot);
    const tabFieldMap = {
      menu:         ['menu_tiles'],
      event:        ['event_details', 'discover_cards'], // event_details + Calendar tile widget
      hotels:       ['travel', 'discover_cards'],
      flights:      ['travel', 'discover_cards'],
      things_to_do: ['things_to_do', 'discover_cards'],
      concierge:    ['concierge', 'discover_cards'],
      celebration:  [], // the Celebration tab saves through its own service
    };
    const fields = tabFieldMap[tabKey] || [];
    return fields.some(f => JSON.stringify(form[f]) !== JSON.stringify(saved[f]));
  }, [form, savedSnapshot]);

  const currentTabDirty = isDirty(activeTab);

  const previewGuest = useMemo(() => {
    if (!previewGuestId) return null;
    return guests.find(g => g.id === previewGuestId) || null;
  }, [previewGuestId, guests]);

  const handleTabSwitch = (tabKey) => {
    if (tabKey === activeTab) return;
    if (currentTabDirty) {
      setPendingTab(tabKey);
      return;
    }
    setActiveTab(tabKey);
    setSavedTab(null);
  };

  const handleSaveTab = async () => {
    setSaving(true);
    setSavedTab(null);
    await onSave(form);
    setSavedSnapshot(JSON.stringify(form));
    setSaving(false);
    setSavedTab(activeTab);
    setTimeout(() => setSavedTab(null), 2500);
  };

  const handleDiscardAndSwitch = () => {
    // Revert current tab to saved state — restore every field the tab tracks
    // (legacy data + discover_cards entry, where applicable).
    const saved = JSON.parse(savedSnapshot);
    const tabFieldMap = {
      menu:         ['menu_tiles'],
      event:        ['event_details', 'discover_cards'],
      hotels:       ['travel', 'discover_cards'],
      flights:      ['travel', 'discover_cards'],
      things_to_do: ['things_to_do', 'discover_cards'],
      concierge:    ['concierge', 'discover_cards'],
      celebration:  [], // the Celebration tab saves through its own service
    };
    const fields = tabFieldMap[activeTab] || [];
    setForm(prev => {
      const next = { ...prev };
      fields.forEach(f => { next[f] = saved[f]; });
      return next;
    });
    setActiveTab(pendingTab);
    setPendingTab(null);
    setSavedTab(null);
  };

  const handleAssign = async (items) => {
    try {
      const res = await fetch(`${API}/api/templates/${form.id}/assign`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignments: items }),
      });
      const data = await res.json();
      setAssignments(prev => [...prev, ...(data.assigned || [])]);
    } catch {}
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      await fetch(`${API}/api/templates/assignments/${assignmentId}`, { method: 'DELETE' });
      setAssignments(prev => prev.filter(a => a.id !== assignmentId));
    } catch {}
  };

  const activeTabLabel = EDITOR_TABS.find(t => t.key === activeTab)?.label || '';

  return (
    <div>
      {/* Unsaved changes modal */}
      {pendingTab && (
        <UnsavedChangesModal
          tabLabel={activeTabLabel}
          onDiscard={handleDiscardAndSwitch}
          onStay={() => setPendingTab(null)}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button data-testid="back-to-list" style={S.btnGhost} onClick={() => {
            // Check if ANY tab is dirty before going back
            const anyDirty = JSON.stringify(form) !== savedSnapshot;
            if (anyDirty) {
              if (!window.confirm('You have unsaved changes. Discard and go back?')) return;
            }
            onBack();
          }}>
            <ArrowLeft size={16} />
          </button>
          <div>
            <h2 style={{ fontFamily: G.cinzel, fontSize: 20, color: G.gold, margin: 0, fontWeight: 700 }}>
              {form.name}
            </h2>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              {form.is_default ? (
                <span style={{ ...S.badge, background: `linear-gradient(135deg, ${G.gold}, ${G.goldTer})`, color: G.dark }}>
                  <Star size={9} /> Default
                </span>
              ) : (
                <span style={{ ...S.badge, background: 'rgba(122,106,64,0.15)', color: G.muted }}>
                  {form.status === 'active' ? 'Active' : 'Draft'}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Template name edit (for custom templates) */}
      {!form.is_default && (
        <div style={{ marginBottom: 20, display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Template Name</label>
            <input data-testid="template-name" style={S.input} value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} />
          </div>
          <div style={{ width: 160 }}>
            <label style={S.label}>Status</label>
            <select data-testid="template-status" style={{ ...S.input, cursor: 'pointer' }} value={form.status}
              onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
            </select>
          </div>
        </div>
      )}

      {/* Split pane: Editor tabs + Preview */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 24, alignItems: 'start' }}>
        {/* Left: Editor */}
        <div>
          {/* Tabs */}
          <div style={{
            display: 'flex', gap: 0, borderBottom: '1px solid rgba(122,106,64,0.2)',
            marginBottom: 24, overflowX: 'auto',
          }}>
            {EDITOR_TABS.map(tab => {
              const isActive = activeTab === tab.key;
              const tabDirty = isDirty(tab.key);
              const Icon = tab.icon;
              return (
                <button key={tab.key} data-testid={`editor-tab-${tab.key}`}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '12px 18px',
                    display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
                    borderBottom: isActive ? `2px solid ${G.gold}` : '2px solid transparent',
                    color: isActive ? G.dark : G.muted, fontFamily: G.cinzel, fontWeight: isActive ? 700 : 500,
                    fontSize: 12, letterSpacing: '0.3px', transition: 'all 0.2s',
                    position: 'relative',
                  }}
                  onClick={() => handleTabSwitch(tab.key)}
                >
                  <Icon size={13} /> {tab.label}
                  {tabDirty && (
                    <span style={{ width: 6, height: 6, borderRadius: 3, background: '#B8902A',
                      position: 'absolute', top: 8, right: 8 }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div style={S.card}>
            {activeTab === 'menu' && (
              <MenuTilesEditor tiles={form.menu_tiles || []}
                onChange={tiles => setForm(prev => ({ ...prev, menu_tiles: tiles }))} />
            )}
            {activeTab === 'hotels' && (
              <div>
                <CarouselTileSection
                  cardId="hotels"
                  cards={form.discover_cards || []}
                  onCardsChange={cards => setForm(prev => ({ ...prev, discover_cards: cards }))}
                  defaultTitle="Hotels"
                  defaultSubtitle="Where to stay"
                />
                <HotelsEditor
                  travel={form.travel || {}}
                  onTravelChange={travel => setForm(prev => ({ ...prev, travel }))}
                />
              </div>
            )}
            {activeTab === 'flights' && (
              <div>
                <CarouselTileSection
                  cardId="flights"
                  cards={form.discover_cards || []}
                  onCardsChange={cards => setForm(prev => ({ ...prev, discover_cards: cards }))}
                  defaultTitle="Flights"
                  defaultSubtitle="Getting here"
                />
                <FlightsEditor
                  travel={form.travel || {}}
                  onTravelChange={travel => setForm(prev => ({ ...prev, travel }))}
                />
              </div>
            )}
            {activeTab === 'things_to_do' && (
              <div>
                <CarouselTileSection
                  cardId="nj-guide"
                  cards={form.discover_cards || []}
                  onCardsChange={cards => setForm(prev => ({ ...prev, discover_cards: cards }))}
                  defaultTitle="NJ Guide"
                  defaultSubtitle="Explore the area"
                />
                <ThingsToDoEditor data={form.things_to_do || {}}
                  onChange={things_to_do => setForm(prev => ({ ...prev, things_to_do }))} />
              </div>
            )}
            {activeTab === 'concierge' && (
              <div>
                <CarouselTileSection
                  cardId="concierge"
                  cards={form.discover_cards || []}
                  onCardsChange={cards => setForm(prev => ({ ...prev, discover_cards: cards }))}
                  defaultTitle="Concierge"
                  defaultSubtitle="Ask me any questions"
                />
                <ConciergeEditor concierge={form.concierge || {}}
                  onChange={concierge => setForm(prev => ({ ...prev, concierge }))} />
              </div>
            )}
            {activeTab === 'event' && (
              <div>
                <EventDetailsEditor details={form.event_details || {}}
                  onChange={event_details => setForm(prev => ({ ...prev, event_details }))} />
                <div style={{ marginTop: 28 }}>
                  <CarouselTileSection
                    cardId="calendar"
                    cards={form.discover_cards || []}
                    onCardsChange={cards => setForm(prev => ({ ...prev, discover_cards: cards }))}
                    defaultTitle="Calendar"
                    defaultSubtitle="See Carol's Schedule"
                  />
                  <p style={{ fontFamily: G.cormorant, fontSize: 12, fontStyle: 'italic',
                    color: G.muted, margin: '0 0 8px' }}>
                    Schedule entries are managed from the Calendar tool. This
                    widget only controls the carousel tile label and visibility.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'celebration' && <CelebrationEditor />}

            {/* Per-tab save footer (the Celebration tab has its own Save) */}
            {activeTab !== 'celebration' && (
              <TabSaveFooter
                dirty={currentTabDirty}
                saving={saving}
                saved={savedTab === activeTab}
                onSave={handleSaveTab}
                tabLabel={activeTabLabel}
              />
            )}

            {/* Assignment section (custom templates only) */}
            {activeTab !== 'celebration' && !form.is_default && (
              <AssignmentPanel
                templateId={form.id}
                assignments={assignments}
                onAssign={handleAssign}
                onRemove={handleRemoveAssignment}
              />
            )}
          </div>
        </div>

        {/* Right: Live Preview */}
        <div style={{ position: 'sticky', top: 20 }}>
          {/* Preview guest selector */}
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Eye size={13} color="#1A1A1A" />
            <span style={{ fontFamily: G.cinzel, fontSize: 10, color: '#1A1A1A', letterSpacing: '1px', textTransform: 'uppercase' }}>
              Preview As
            </span>
            <select
              data-testid="preview-guest-select"
              style={{ ...S.input, padding: '4px 8px', fontSize: 12, width: 'auto', flex: 1, background: 'rgba(255,252,240,0.2)' }}
              value={previewGuestId}
              onChange={e => setPreviewGuestId(e.target.value)}
            >
              <option value="">Default (no personalization)</option>
              {guests.map(g => (
                <option key={g.id} value={g.id}>{g.first_name} {g.last_name}</option>
              ))}
            </select>
          </div>
          <LivePreview template={form} previewGuest={previewGuest} />
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function GuestExperienceEditor() {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/templates`);
      const data = await res.json();
      setTemplates(data);
    } catch {}
    setLoading(false);
  }, []);

  const fetchGuests = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/guests`);
      setGuests(await res.json());
    } catch {}
  }, []);

  useEffect(() => { fetchTemplates(); fetchGuests(); }, [fetchTemplates, fetchGuests]);

  const handleSelect = async (t) => {
    try {
      const res = await fetch(`${API}/api/templates/${t.id}`);
      const full = await res.json();
      setSelectedTemplate(full);
    } catch {}
  };

  const handleCreate = async () => {
    try {
      const defaultTpl = templates.find(t => t.is_default);
      const body = defaultTpl
        ? { name: 'Custom Guest Experience', menu_tiles: defaultTpl.menu_tiles, discover_cards: defaultTpl.discover_cards,
            travel: defaultTpl.travel, concierge: defaultTpl.concierge, event_details: defaultTpl.event_details }
        : { name: 'Custom Guest Experience' };
      const res = await fetch(`${API}/api/templates`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const created = await res.json();
      await fetchTemplates();
      setSelectedTemplate({ ...created, assignments: [] });
    } catch {}
  };

  const handleDuplicate = async (id) => {
    try {
      await fetch(`${API}/api/templates/${id}/duplicate`, { method: 'POST' });
      fetchTemplates();
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template? All assignments will be removed.')) return;
    try {
      await fetch(`${API}/api/templates/${id}`, { method: 'DELETE' });
      fetchTemplates();
    } catch {}
  };

  const handleSave = async (form) => {
    try {
      await fetch(`${API}/api/templates/${form.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      fetchTemplates();
    } catch {}
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Sparkles size={24} color={G.gold} style={{ animation: 'spin 2s linear infinite' }} />
        <p style={{ fontFamily: G.cormorant, fontSize: 16, color: G.muted, marginTop: 12 }}>Loading templates...</p>
      </div>
    );
  }

  if (selectedTemplate) {
    return (
      <TemplateEditorView
        template={selectedTemplate}
        guests={guests}
        onBack={() => { setSelectedTemplate(null); fetchTemplates(); }}
        onSave={handleSave}
      />
    );
  }

  return (
    <TemplateListView
      templates={templates}
      onSelect={handleSelect}
      onCreate={handleCreate}
      onDuplicate={handleDuplicate}
      onDelete={handleDelete}
    />
  );
}
